import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { generateCapabilityConfigCandidate } from '@/services/capabilityConfigManagement';

const { Paragraph } = Typography;
const { TextArea } = Input;

const DIMENSION_OPTIONS = [
  ['coordination', '协调性'],
  ['lower_body_stability', '下肢稳定性'],
  ['trunk_stability', '躯干稳定性'],
  ['upper_body_stability', '上肢稳定性'],
  ['upper_body_mobility', '上肢活动度'],
  ['lower_body_mobility', '下肢活动度'],
  ['trunk_mobility', '躯干活动度'],
  ['lower_body_strength', '下肢力量'],
  ['upper_body_strength', '上肢力量'],
  ['cardiovascular_endurance', '心肺耐力'],
].map(([value, label]) => ({ value, label }));

function isSuccess(response) {
  return response && (response.code === '00000' || response.code === '0');
}

/** 生成能力配置候选；通过静态校验后可直接采用到手动编排区。 */
export default function CapabilityConfigAiGenerator({
  configType,
  scopeKey,
  currentConfig,
  onApply,
}) {
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const evidenceRecipe = configType === 'evidence_recipe';

  const generate = async () => {
    const values = await form.validateFields();
    const actionId = evidenceRecipe ? values.actionId : Number(scopeKey);
    setGenerating(true);
    try {
      const response = await generateCapabilityConfigCandidate({
        configType,
        scopeKey,
        actionId,
        dimension: values.dimension,
        actionDescription: values.actionDescription,
        generationRequirement: values.generationRequirement,
        currentConfigContent: values.includeCurrent ? currentConfig : undefined,
      });
      if (!isSuccess(response)) {
        message.error(response?.msg || 'AI 候选生成失败');
        return;
      }
      setCandidate(response.data);
      if (response.data?.validationPassed) {
        message.success('配置已生成，可直接采用到手动编排区');
      } else {
        message.warning('候选已生成，但未通过静态校验');
      }
    } catch (error) {
      message.error(error?.message || 'AI 候选生成失败');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card size="small" title="AI 生成" extra={<RobotOutlined />}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="AI 生成不会自动保存或发布"
          description="仅发送动作名称、动作说明、可选当前配置和生成要求。生成结果先经过静态校验；通过后可直接采用，并继续手动调整。"
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{ includeCurrent: !!currentConfig }}
        >
          <Descriptions
            size="small"
            column={2}
            bordered
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="配置类型">
              {evidenceRecipe ? '证据配方' : '动作能力画像'}
            </Descriptions.Item>
            <Descriptions.Item label="作用域">{scopeKey}</Descriptions.Item>
          </Descriptions>
          {evidenceRecipe && (
            <>
              <Form.Item
                name="actionId"
                label="关联动作 ID"
                rules={[{ required: true, message: '请输入关联动作 ID' }]}
              >
                <InputNumber min={1} precision={0} style={{ width: 220 }} />
              </Form.Item>
              <Form.Item
                name="dimension"
                label="目标能力维度"
                rules={[{ required: true, message: '请选择目标能力维度' }]}
              >
                <Select style={{ width: 280 }} options={DIMENSION_OPTIONS} />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="actionDescription"
            label="动作客观说明补充（可选）"
            tooltip="服务端会自动补充动作库中的客观资料"
          >
            <TextArea rows={3} maxLength={8000} showCount />
          </Form.Item>
          <Form.Item name="generationRequirement" label="本次生成要求">
            <TextArea
              rows={3}
              maxLength={4000}
              showCount
              placeholder="只描述希望纳入的客观能力关联或证据字段，不写评分和好坏判断。"
            />
          </Form.Item>
          <Form.Item name="includeCurrent" valuePropName="checked">
            <Checkbox disabled={!currentConfig}>基于当前配置调整</Checkbox>
          </Form.Item>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            loading={generating}
            onClick={generate}
          >
            生成候选
          </Button>
        </Form>

        {candidate && (
          <Card
            size="small"
            title={`候选 #${candidate.candidateId || '-'}`}
            extra={
              <Tag color={candidate.validationPassed ? 'green' : 'red'}>
                {candidate.validationPassed ? '可采用' : '校验未通过'}
              </Tag>
            }
          >
            <Paragraph
              copyable={!!candidate.candidateContent}
              style={{
                maxHeight: 460,
                overflow: 'auto',
                padding: 14,
                whiteSpace: 'pre-wrap',
                background: '#0f172a',
                borderRadius: 6,
                color: '#d1fae5',
                fontFamily: 'SFMono-Regular, Consolas, monospace',
                fontSize: 13,
              }}
            >
              {candidate.candidateContent || '模型未返回可用候选'}
            </Paragraph>
            <Button
              type="primary"
              disabled={
                !candidate.validationPassed || !candidate.candidateContent
              }
              onClick={() => onApply(candidate.candidateContent)}
            >
              采用并进入手动编排
            </Button>
          </Card>
        )}
      </Space>
    </Card>
  );
}

CapabilityConfigAiGenerator.propTypes = {
  configType: PropTypes.oneOf(['action_profile', 'evidence_recipe']).isRequired,
  scopeKey: PropTypes.string.isRequired,
  currentConfig: PropTypes.string,
  onApply: PropTypes.func.isRequired,
};

CapabilityConfigAiGenerator.defaultProps = {
  currentConfig: '',
};
